import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, ArrowRight } from "lucide-react";
import { Particles } from "./FX";

const VIDEO_URL = "/hero.mp4";
const POSTER_URL = "/hero-poster.webp";

const line = {
  hidden: { opacity: 0, y: 46 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.9, delay: 0.25 + i * 0.16, ease: [0.22, 1, 0.36, 1] } }),
};

export const CinematicHero = ({ onBrowse }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} className="relative h-screen min-h-[600px] overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ scale: videoScale }}>
        <img src={POSTER_URL} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
        <video
          className="relative w-full h-full object-cover"
          src={VIDEO_URL}
          poster={POSTER_URL}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          data-testid="hero-video"
        />
        {/* Dark cinematic grading */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/35 to-[#050505]" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-black/40" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 30% 45%, transparent 30%, rgba(0,0,0,0.55) 100%)" }} />
      </motion.div>

      <Particles density={28} />

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex flex-col justify-center"
      >
        <motion.div custom={0} initial="hidden" animate="show" variants={line}>
          <span className="inline-flex items-center gap-2.5 text-[11px] sm:text-xs font-bold tracking-[0.35em] uppercase text-[#D4AF37] mb-6 sm:mb-8">
            <span className="w-8 h-px bg-[#D4AF37]/60" />
            Alpine Car — Ausztria
          </span>
        </motion.div>

        <h1 className="font-display font-extrabold leading-[0.98] text-white text-[13vw] sm:text-6xl lg:text-[5.2rem] max-w-4xl">
          <motion.span className="block" custom={1} initial="hidden" animate="show" variants={line}>
            Ellenőrzött autók.
          </motion.span>
          <motion.span className="block" custom={2} initial="hidden" animate="show" variants={line}>
            Ausztriából.
          </motion.span>
          <motion.span
            className="block text-transparent bg-clip-text bg-gradient-to-r from-[#F0D77B] via-[#D4AF37] to-[#8f6f1d]"
            custom={3} initial="hidden" animate="show" variants={line}
          >
            Biztonságosabb vásárlás.
          </motion.span>
        </h1>

        <motion.p
          custom={4} initial="hidden" animate="show" variants={line}
          className="mt-6 sm:mt-8 text-base sm:text-lg text-white/60 max-w-xl leading-relaxed"
        >
          Ausztriából származó, gondosan átvizsgált autók. Mi ellenőrizzük a járművet és intézzük a szükséges ügyintézést, hogy Ön magabiztosabban dönthessen.
        </motion.p>

        <motion.div custom={5} initial="hidden" animate="show" variants={line} className="mt-8 sm:mt-10 flex">
          <button
            onClick={onBrowse}
            className="btn-gold rounded-full font-bold px-8 sm:px-10 py-4 text-sm sm:text-base inline-flex items-center justify-center gap-2"
            data-testid="hero-cars-btn"
          >
            Elérhető autók <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={onBrowse}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/40 hover:text-[#D4AF37] transition-colors"
        data-testid="hero-scroll-hint"
        aria-label="Görgetés lefelé"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">Görgetés</span>
        <ChevronDown className="w-5 h-5 scroll-hint" />
      </motion.button>
    </section>
  );
};
